<template>
    <div id="hey-supsis" :class="{ open, listening }">
        <span class="message" v-if="commandProcessing">{{ command }}</span>
        <span class="message" v-else>{{ message }}</span>
        <div class="rotating-border rotating-border--rainbow" v-if="commandProcessing">
            <MicButton :active="listening" />
        </div>
        <MicButton :active="listening" v-else />
    </div>
</template>
<script>
import Vue from "vue";
import SpeechToTextService from "../../utils/speech/SpeechToTextService";
import MicButton from "../../utils/speech/MicButton.vue";
import { mapActions, mapState } from "vuex";
import { sendSocketMessage, socketMessageStream } from "../../utils/socketStream";
import log2 from "../../utils/log2";
import * as Rx from "rxjs";
import SYSTEM_PUBSUB, { systemMessageStream, SystemMessageTypes } from "../../utils/systemPubSub";
import { filter } from "rxjs/operators";

const log = log2("HeySupsis");
export default Vue.extend({
    name: "HeySupsis",
    components: { MicButton },
    data() {
        return {
            open: false,
            listening: false,
            speechRecognition: null,
            message: "",
            command: "",
            commandProcessing: false,
            continuousMode: false,
            cancelTokens: [],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        deactivate() {
            this.open = false;
            this.message = "";
            this.command = "";
            this.commandProcessing = false;
            this.continuousMode = false;
        },

        startRecognition() {
            log("startRecognition");
            const fn = () => {
                this.stopRecognition();
                this.speechRecognition = new SpeechToTextService(this.continuousMode);
                this.listening = true;
                this.speechRecognition.addEventListener("text", (e) => {
                    const message = e.detail || "";
                    log("recognition text", message);
                    this.message = message;
                });
                this.speechRecognition.addEventListener("end", (e) => {
                    log("recognition end", e);
                    const message = e.detail || "";
                    if (message.length) {
                        this.message = message;

                        if (!this.commandProcessing) {
                            this.command = message;
                            this.sendCommand(this.command);
                        }
                    }
                    this.stopRecognition();
                });
                this.speechRecognition.addEventListener("error", (e) => {
                    log("recognition error", e);
                    this.stopRecognition();
                    this.deactivate();
                });
            };

            this.$feature.beforeUseChatCopilot(fn);
        },
        stopRecognition() {
            log("stopRecognition");
            this.listening = false;
            if (this.speechRecognition) this.speechRecognition.stop();

            this.message = "";
        },
        sendCommand(command) {
            log("sendCommand", command, this.$route);

            const fn = () => {
                if (!this.commandProcessing) {
                    this.commandProcessing = true;
                    const isAIImplementedPage = ["ticket"].includes(this.$route.name);
                    if (isAIImplementedPage) {
                        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.VOICE_COMMAND_RECEIVED, {
                            command,
                        });
                    } else {
                        sendSocketMessage("user ai-command", {
                            siteId: this.site._id,
                            userId: this.user._id,
                            command: command,
                        });
                    }
                }
            };
            this.$feature.isChatCopilotEnabled(fn);
        },
        handleResponse(response) {
            if (!this.commandProcessing) return;
            const { success, functionName, functionArgs, context } = response;
            log("handleResponse", response);
            const FUNCTION_TYPES = {
                CREATE_TASK: "create_task",
                CREATE_DM: "create_dm",
            };
            this.deactivate();
            if (!success) {
                this.$message.error("Sorry, I didn't understand that.");
            } else {
                if (context) {
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.VOICE_ASSISTANT_RESPONSE_RECEIVED, {
                        ...response,
                    });
                } else if (functionName === FUNCTION_TYPES.CREATE_TASK) {
                    let formDefaults = {
                        title: functionArgs.title,
                        description: functionArgs.description,
                        startDate: functionArgs.startDate,
                        endDate: functionArgs.endDate,
                        priority: functionArgs.priority,
                        ownerId: functionArgs.ownerId,
                        assigneeIds: functionArgs.assigneeIds,
                    };
                    //delete null values
                    formDefaults = Object.keys(formDefaults).reduce((acc, key) => {
                        if (formDefaults[key] !== null) {
                            acc[key] = formDefaults[key];
                        }
                        return acc;
                    }, {});

                    this.addNewWindow({
                        type: "create-task",
                        payload: {
                            formDefaults,
                        },
                    });
                } else if (functionName === FUNCTION_TYPES.CREATE_DM) {
                    const { destinationUserId, message } = functionArgs;
                    if (destinationUserId) {
                        this.$router.push("/chat?tabIndex=6");

                        setTimeout(() => {
                            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.START_AGENT_CHAT, {
                                userId: destinationUserId,
                                message,
                            });
                        }, 500);
                    }
                }
            }
        },
        listen() {
            log("listen");
            this.sub = socketMessageStream.subscribe(({ cmd, data }) => {
                if (cmd === "ai_command_response") {
                    log("ai_command_response", data);

                    this.handleResponse(data);
                }
            });
            this.sub2 = systemMessageStream
                .pipe(filter((x) => x.type === SystemMessageTypes.ACTIVE_VOICE_ASSISTANT))
                .subscribe(() => {
                    this.$feature.beforeUseChatCopilot(() => {
                        this.open = true;
                    });
                });
        },
        setupPressAndTalk() {
            //start recognition with (CTRL + ")
            const subject = new Rx.Subject();

            const keydownFN = (event) => {
                const isSkey = event.key === '"';
                const isESC = event.key === "Escape";
                log("setupPressAndTalk.keydownFN", { key: event.key, isSkey, isESC, repeat: event.repeat });

                if (isSkey && event.repeat) {
                    subject.next(true);
                }
                if (isESC && this.open) {
                    this.deactivate();
                }
            };
            const keyupFN = (event) => {
                const isSkey = event.key === '"';

                if (isSkey) {
                    subject.next(false);
                }
            };
            document.addEventListener("keydown", keydownFN);

            document.addEventListener("keyup", keyupFN);

            subject.subscribe((isActive) => {
                const fn = () => {
                    if (isActive) {
                        if (!this.open) {
                            this.continuousMode = true;

                            this.open = true;
                        }
                    } else {
                        if (this.speechRecognition) {
                            this.stopRecognition();
                            this.sendCommand(this.command);
                        }
                    }
                };
                this.$feature.isChatCopilotEnabled(fn);
            });
            this.cancelTokens.push(() => {
                document.removeEventListener("keydown", keydownFN);
                document.removeEventListener("keyup", keyupFN);
                subject.unsubscribe();
            });
            //window events and is no event cancel recogniton
        },
    },
    mounted() {
        this.listen();
        this.setupPressAndTalk();
        if (this.open) {
            this.startRecognition();
        }
    },
    beforeDestroy() {
        this.stopRecognition();
        this.sub && this.sub.unsubscribe();
        this.sub2 && this.sub2.unsubscribe();
        this.cancelTokens.forEach((fn) => fn());
    },
    watch: {
        open() {
            if (this.open) {
                this.message = "";
                this.command = "";
                this.startRecognition();
            } else {
                this.stopRecognition();
            }
        },
        commandProcessing(isActive) {
            if (isActive) {
                this.lastProcessTimerId = setTimeout(() => {
                    if (this.commandProcessing) {
                        this.$message.error("Sorry, I didn't understand that.");
                        this.commandProcessing = false;
                    }
                }, 10000);
            } else {
                if (this.lastProcessTimerId) {
                    clearTimeout(this.lastProcessTimerId);
                }
            }
        },
    },
});
</script>
<style lang="scss">
.material-icons {
    &.spin {
        animation: spin 1s linear infinite;
    }
}
</style>
<style scoped lang="scss">
#hey-supsis {
    display: none;

    .mic-button {
        position: relative !important;
        height: 30px;
        width: 30px;
    }

    &.open {
        position: fixed;
        top: 10px;
        right: 10px;
        min-width: 200px;
        height: 50px;
        z-index: 999;
        background-color: rgba(255, 255, 255, 0.9);
        border: 1px solid black;
        border-radius: 50px;
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 10px;

        .message {
            font-size: 1.2rem;
            font-weight: bold;
            color: black;
            text-shadow: 0 0 10px white;
        }
    }

    @keyframes spin {
        from {
            transform: translate(-50%, -50%) rotate(0);
        }
        to {
            transform: translate(-50%, -50%) rotate(360deg);
        }
    }

    .rotating-border {
        --border-radius: 1rem;
        --border-size: 2px;
        --border-bg: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);

        position: relative;
        overflow: hidden;
        font-size: 2rem;
        padding: calc(var(--border-size));
        border-radius: var(--border-radius);
        display: inline-block;

        &::before {
            content: "";
            display: block;
            background: var(--border-bg);
            width: calc(100% * 1.41421356237);
            padding-bottom: calc(100% * 1.41421356237);
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            border-radius: 100%;
            z-index: -2;
            animation: spin 5s linear infinite;
        }

        &--reverse::before {
            animation-direction: reverse;
        }

        &::after {
            content: "";
            position: absolute;
            inset: var(--border-size);
            background: white;
            z-index: -1;
            border-radius: calc(var(--border-radius) - var(--border-size));
        }
    }

    .rotating-border--rainbow {
        --border-radius: 1.5rem;
        --border-size: 5px;
        --border-bg: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
    }
}
</style>
